import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useCallback, useRef } from 'react';
import { dashboardApi, reportsApi, alertsApi } from '@/services/api';
import { useSocketEvent } from '@/services/socket';
import CollapsibleCard from '@/components/CollapsibleCard';
import { toast } from 'react-hot-toast';
import {
  alertTypeName,
  alertTypeIcon,
  isTamperAlert,
} from '@/constants/alertTypes';
import { useAuthStore } from '@/stores/authStore';
import {
  TruckIcon,
  BellAlertIcon,
  BuildingOffice2Icon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  SignalSlashIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SignalIcon,
  MapPinIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  WifiIcon,
  BoltIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  CpuChipIcon,
  GlobeAltIcon,
  FireIcon,
  HeartIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  BarChart,
  Bar,
  Legend,
  LineChart,
  Line,
  RadialBarChart,
  RadialBar,
  ComposedChart,
} from 'recharts';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ar';
import { useMemo, useState, useEffect } from 'react';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ar');

// Timestamps are rendered in the fleet's timezone, not the browser's — an
// operator abroad must still read local Libyan time.
const TZ = 'Africa/Tripoli';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const severityColors: Record<string, string> = {
  low: 'bg-success-100 text-success-700',
  medium: 'bg-warning-100 text-warning-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-emergency-100 text-emergency-700',
};

// Labels and icons come from the shared alert table (`alertTypeName` /
// `alertTypeIcon`), so this panel and the alerts page describe the same event
// the same way. This file used to keep its own list and draw one bell icon
// for every type regardless of what had happened.

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'danger' | 'purple' | 'gray';
  trend?: { value: number; isUp: boolean };
  /** Where the tile drills through to. Omitted for figures with no detail view. */
  to?: string;
}

function KPICard({ title, value, subtitle, icon, color, trend, to }: KPICardProps) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600 border-primary-200',
    success: 'bg-success-50 text-success-600 border-success-200',
    warning: 'bg-warning-50 text-warning-600 border-warning-200',
    danger: 'bg-emergency-50 text-emergency-600 border-emergency-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  };

  const iconBgClasses = {
    primary: 'bg-primary-100',
    success: 'bg-success-100',
    warning: 'bg-warning-100',
    danger: 'bg-emergency-100',
    purple: 'bg-purple-100',
    gray: 'bg-gray-100',
  };

  const body = (
    <div className={clsx('card border h-full', colorClasses[color],
      to && 'transition-shadow hover:shadow-lg cursor-pointer')}>
      <div className="card-body p-3 sm:p-4 3xl:p-6 4xl:p-8 5xl:p-10">
        <div className="flex items-start justify-between">
          <div className={clsx('p-2 sm:p-2.5 3xl:p-3 4xl:p-4 5xl:p-5 rounded-lg 3xl:rounded-xl 4xl:rounded-2xl', iconBgClasses[color])}>
            <div className="w-5 h-5 sm:w-6 sm:h-6 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 5xl:w-12 5xl:h-12">
              {icon}
            </div>
          </div>
          {trend && (
            <div className={clsx(
              'flex items-center gap-1 text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl font-medium px-2 py-1 3xl:px-3 3xl:py-1.5 4xl:px-4 4xl:py-2 rounded-full',
              trend.isUp ? 'bg-success-100 text-success-700' : 'bg-emergency-100 text-emergency-700'
            )}>
              {trend.isUp ? (
                <ArrowTrendingUpIcon className="w-3 h-3 3xl:w-4 3xl:h-4 4xl:w-5 4xl:h-5 5xl:w-6 5xl:h-6" />
              ) : (
                <ArrowTrendingDownIcon className="w-3 h-3 3xl:w-4 3xl:h-4 4xl:w-5 4xl:h-5 5xl:w-6 5xl:h-6" />
              )}
              {trend.value}%
            </div>
          )}
        </div>
        <div className="mt-2 sm:mt-3 3xl:mt-4 4xl:mt-5 5xl:mt-6">
          <p className="text-xl sm:text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl font-medium text-gray-600">{title}</p>
          {subtitle && (
            <p className="text-xs 3xl:text-sm 4xl:text-base 5xl:text-lg text-gray-500 mt-1 3xl:mt-2">{subtitle}</p>
          )}
          {to && (
            <p className="text-[11px] text-primary-600 mt-1 flex items-center gap-1">
              عرض التفاصيل
              <ChevronLeftIcon className="w-3 h-3" />
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Link, never <a href>: an anchor reloads the document and throws away the
  // whole dashboard's loaded state.
  return to ? <Link to={to} className="block h-full">{body}</Link> : body;
}

export default function Dashboard() {
  const { user } = useAuthStore();
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('today');
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Calculate date range based on selected period
  // Calendar dates, not instants. `dayjs().startOf('day').toISOString()`
  // converts to UTC first, so at UTC+2 "today" became yesterday's date once
  // the server took the first ten characters.
  const dateRange = useMemo(() => {
    const now = dayjs();
    const days = selectedPeriod === 'today' ? 0 : selectedPeriod === 'week' ? 7 : 30;
    return {
      startDate: now.subtract(days, 'day').format('YYYY-MM-DD'),
      endDate: now.format('YYYY-MM-DD'),
    };
  }, [selectedPeriod]);

  // Live invalidation — debounced.
  //
  // Without debounce a busy fleet (10 devices reporting every 30s →
  // ~3 events/s, but bursts of 10+ events in <1s during reconnects)
  // would invalidate the same queries dozens of times per second. React
  // Query's dedup helps but it still issues one refetch per invalidate,
  // and the user's tab can easily exceed the rate limit on its own.
  //
  // Strategy: coalesce all invalidations within a 1500ms window into a
  // single refetch. Live feel stays sub-2s, traffic drops by an order
  // of magnitude.
  const queryClient = useQueryClient();
  const positionTimer = useRef<number | null>(null);
  const alertTimer = useRef<number | null>(null);

  const onPosition = useCallback(() => {
    if (positionTimer.current) return;
    positionTimer.current = window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['zone-breaches'] });
      positionTimer.current = null;
      // 10s, not 1.5s: a fleet reporting every 30 seconds produced a refetch
      // burst roughly every second and a half, which is what exhausted the
      // request budget. The figures here are counts, not live positions —
      // ten seconds is well inside what an operator perceives as live.
    }, 10_000);
  }, [queryClient]);

  const onAlert = useCallback(() => {
    if (alertTimer.current) return;
    alertTimer.current = window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['recent-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      alertTimer.current = null;
    }, 1500);
  }, [queryClient]);

  useSocketEvent('position:update', onPosition);
  useSocketEvent('alert:new', onAlert);

  useEffect(() => () => {
    if (positionTimer.current) clearTimeout(positionTimer.current);
    if (alertTimer.current) clearTimeout(alertTimer.current);
  }, []);

  // staleTime on every polled query below, and why it matters.
  //
  // These queries are invalidated from three directions at once: the socket
  // handlers above, the resolve mutation, and their own refetchInterval. With
  // no staleTime, React Query treats data as stale the instant it arrives, so
  // every one of those triggers an immediate network round trip. In production
  // that produced four overlapping /dashboard/stats requests inside sixteen
  // seconds while earlier ones were still in flight.
  //
  // staleTime slightly below the poll interval collapses the duplicates
  // without making the figures any less current: an invalidation inside the
  // window is served from cache, and the next real refresh still lands on
  // schedule.
  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await dashboardApi.getStats();
      return response.data.data;
    },
    refetchInterval: 30000,
    staleTime: 25_000,
    refetchOnWindowFocus: false,
  });

  // Fetch recent alerts using alertsApi
  const { data: recentAlerts } = useQuery({
    queryKey: ['recent-alerts'],
    queryFn: async () => {
      const response = await alertsApi.getAll({ limit: 10 });
      return response.data.data?.alerts || response.data.data;
    },
    refetchInterval: 30000,
    staleTime: 25_000,
    refetchOnWindowFocus: false,
  });

  // Fetch report summary for the selected period
  const { data: reportSummary } = useQuery({
    queryKey: ['report-summary', dateRange],
    queryFn: async () => {
      const response = await reportsApi.getSummary({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      return response.data.data;
    },
  });

  // Fetch daily activity for charts
  const { data: dailyActivityData } = useQuery({
    queryKey: ['daily-activity', dateRange],
    queryFn: async () => {
      const response = await reportsApi.getDailyActivity({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      return response.data.data;
    },
  });

  // Fetch hourly distribution
  const { data: hourlyDistributionData } = useQuery({
    queryKey: ['hourly-distribution', dateRange],
    queryFn: async () => {
      const response = await reportsApi.getHourlyDistribution({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      return response.data.data;
    },
  });

  // Fetch vehicle types distribution
  const { data: vehicleTypesData } = useQuery({
    queryKey: ['vehicle-types'],
    queryFn: async () => {
      const response = await reportsApi.getVehicleTypes();
      return response.data.data;
    },
  });

  // Fetch top vehicles
  const { data: topVehiclesData } = useQuery({
    queryKey: ['top-vehicles', dateRange],
    queryFn: async () => {
      const response = await reportsApi.getTopVehicles({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit: 5,
      });
      return response.data.data;
    },
  });

  // Fetch alerts summary
  const { data: alertsSummaryData } = useQuery({
    queryKey: ['alerts-summary', dateRange],
    queryFn: async () => {
      const response = await reportsApi.getAlertsSummary({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      return response.data.data;
    },
  });


  // Vehicles currently outside their organization's operating radius.
  const { data: zoneBreachData } = useQuery({
    queryKey: ['zone-breaches'],
    queryFn: async () => {
      const r = await dashboardApi.getZoneBreaches();
      return { items: r.data.data ?? [], meta: r.data.meta ?? {} };
    },
    refetchInterval: 30000,
    staleTime: 25_000,
    refetchOnWindowFocus: false,
  });

  // Resolving from the dashboard: the card lists unresolved tampering, so a
  // resolved alert drops out of it. Both lists are invalidated so the panel
  // below and the badge counts stay in step.
  const resolveAlertMutation = useMutation({
    mutationFn: (id: string) => alertsApi.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tamper-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['recent-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('تم وضع حل للتنبيه');
    },
    onError: () => toast.error('تعذّر تحديث التنبيه'),
  });

  // Tampering and crashes still open. The alerts endpoint filters one type at
  // a time, so this pulls the unresolved set once and narrows it here rather
  // than firing a request per type.
  const { data: tamperAlerts } = useQuery({
    queryKey: ['tamper-alerts'],
    queryFn: async () => {
      const r = await alertsApi.getAll({ isResolved: false, limit: 50 });
      const list = r.data.data?.alerts ?? r.data.data ?? [];
      return (list as any[]).filter((a) => isTamperAlert(a.type));
    },
    refetchInterval: 30000,
    staleTime: 25_000,
    refetchOnWindowFocus: false,
  });

  // Vehicles that have gone silent — no packet received for the configured
  // window (default 6 h). Measured on our receipt clock, not the tracker's.
  const { data: noSignalData } = useQuery({
    queryKey: ['no-signal'],
    queryFn: async () => {
      const r = await dashboardApi.getNoSignal();
      return { items: r.data.data ?? [], meta: r.data.meta ?? {} };
    },
    refetchInterval: 60000,
    staleTime: 55_000,
    refetchOnWindowFocus: false,
  });

  // Fetch system health
  const { data: systemHealthData } = useQuery({
    queryKey: ['system-health'],
    queryFn: async () => {
      const response = await dashboardApi.getHealth();
      return response.data.data;
    },
    refetchInterval: 60000,
    staleTime: 55_000,
    refetchOnWindowFocus: false,
  });

  // Calculate online vehicles from real data
  // Connectivity comes from the server, which decides it once for every
  // caller. Deriving it here from position timestamps meant this figure and
  // the vehicles page could disagree about the same fleet at the same moment.
  const onlineVehicles = stats?.vehicles?.online ?? 0;

  // Vehicle status distribution from real data
  const vehicleStatusData = stats ? [
    { name: 'متصلة', value: onlineVehicles, color: '#22c55e' },
    { name: 'غير متصلة (مطفأة)', value: stats.vehicles?.offline ?? 0, color: '#9ca3af' },
    { name: 'صيانة', value: stats.vehicles?.maintenance || 0, color: '#f59e0b' },
  ] : [];

  // Average response time from report summary
  const avgResponseTime = useMemo(() => {
    return reportSummary?.avgResponseTime || 0;
  }, [reportSummary]);

  // Calculate fleet utilization from real data
  const fleetUtilization = useMemo(() => {
    if (!stats?.vehicles?.total) return 0;
    return Math.round(((stats.vehicles?.onMission || 0) / stats.vehicles.total) * 100);
  }, [stats]);

  // System health — read from the server's own measurements.
  //
  // These were previously invented in the browser: uptime was `dbHealthy ?
  // 99.9 : 0`, server load was the constant 25, and GPS accuracy was bucketed
  // from the last position age. The backend already reports real load average,
  // memory, process uptime and a true reporting ratio; all of it was discarded.
  const systemHealth = useMemo(() => ({
    dbHealthy: systemHealthData?.database === 'healthy',
    uptimeFormatted: systemHealthData?.uptimeFormatted ?? '—',
    serverLoad: systemHealthData?.serverLoad ?? 0,
    cpuCount: systemHealthData?.cpuCount ?? 0,
    memoryMB: systemHealthData?.memory?.rssMB ?? 0,
    gpsAccuracy: systemHealthData?.gpsAccuracy ?? 0,
    reportingVehicles: systemHealthData?.reportingVehicles ?? 0,
    totalVehicles: systemHealthData?.totalVehicles ?? 0,
    lastPositionAge: systemHealthData?.lastPositionAgeFormatted || 'غير متاح',
  }), [systemHealthData]);

  const healthData = [
    { name: 'مركبات مُبلِّغة', value: systemHealth.gpsAccuracy, fill: '#3b82f6' },
    { name: 'سعة الخادم المتاحة', value: Math.max(0, 100 - systemHealth.serverLoad), fill: '#f59e0b' },
    { name: 'قاعدة البيانات', value: systemHealth.dbHealthy ? 100 : 0, fill: '#22c55e' },
  ];

  // Top vehicles from API
  const topVehicles = useMemo(() => {
    if (!topVehiclesData || !Array.isArray(topVehiclesData)) return [];
    return topVehiclesData.map((v: any) => ({
      name: v.plate || v.plateNumber,
      trips: v.trips || 0,
      distance: v.distance || 0,
      activeDays: v.activeDays || 0,
    }));
  }, [topVehiclesData]);

  // Format daily activity for chart
  const activityChartData = useMemo(() => {
    if (!dailyActivityData || !Array.isArray(dailyActivityData)) return [];
    return dailyActivityData.map((d: any) => ({
      date: dayjs(d.date).format('MM/DD'),
      day: dayjs(d.date).format('ddd'),
      trips: d.trips || 0,
      vehicles: d.activeVehicles || 0,
      distance: d.distance || 0,
      alerts: d.alerts || 0,
    }));
  }, [dailyActivityData]);

  // Format hourly distribution for chart
  const hourlyChartData = useMemo(() => {
    if (!hourlyDistributionData || !Array.isArray(hourlyDistributionData)) return [];
    return hourlyDistributionData.map((h: any) => ({
      hour: `${String(h.hour).padStart(2, '0')}:00`,
      count: h.count || 0,
      distance: h.distance || 0,
    }));
  }, [hourlyDistributionData]);

  // Alerts by severity from API
  const alertsBySeverity = useMemo(() => {
    if (!alertsSummaryData) return [];
    
    // alertsSummaryData is an array of { type, severity, count }
    if (Array.isArray(alertsSummaryData)) {
      const severityCounts: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
      alertsSummaryData.forEach((item: any) => {
        if (item.severity && severityCounts.hasOwnProperty(item.severity)) {
          severityCounts[item.severity] += Number(item.count) || 0;
        }
      });
      return [
        { name: 'حرج', value: severityCounts.critical, color: '#dc2626' },
        { name: 'عالي', value: severityCounts.high, color: '#f97316' },
        { name: 'متوسط', value: severityCounts.medium, color: '#eab308' },
        { name: 'منخفض', value: severityCounts.low, color: '#22c55e' },
      ];
    }
    
    // Fallback for object format
    return [
      { name: 'حرج', value: alertsSummaryData.critical || 0, color: '#dc2626' },
      { name: 'عالي', value: alertsSummaryData.high || 0, color: '#f97316' },
      { name: 'متوسط', value: alertsSummaryData.medium || 0, color: '#eab308' },
      { name: 'منخفض', value: alertsSummaryData.low || 0, color: '#22c55e' },
    ];
  }, [alertsSummaryData]);

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="spinner w-10 h-10 sm:w-12 sm:h-12 3xl:w-16 3xl:h-16 4xl:w-20 4xl:h-20 5xl:w-24 5xl:h-24" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 3xl:space-y-8 4xl:space-y-10 5xl:space-y-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4 3xl:gap-6">
        <div>
          <h1 className="text-xl sm:text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl font-bold text-gray-900">لوحة التحكم</h1>
          <p className="text-sm sm:text-base 3xl:text-lg 4xl:text-xl 5xl:text-2xl text-gray-600 mt-1 3xl:mt-2">
            مرحباً {user?.fullName}، آخر تحديث: {dayjs(currentTime).format('HH:mm:ss')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            {(['today', 'week', 'month'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={clsx(
                  'px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm 3xl:px-4 3xl:py-2 3xl:text-base 4xl:px-5 4xl:py-2.5 4xl:text-lg 5xl:px-6 5xl:py-3 5xl:text-xl rounded-md 3xl:rounded-lg transition-all',
                  selectedPeriod === period 
                    ? 'bg-white shadow text-primary-600 font-medium' 
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                {period === 'today' ? 'اليوم' : period === 'week' ? 'الأسبوع' : 'الشهر'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* System status — every figure here is measured server-side. */}
      {(() => {
        const healthy = systemHealth.dbHealthy && systemHealth.serverLoad < 85;
        return (
          <div className={clsx(
            'rounded-lg sm:rounded-xl p-3 sm:p-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 sm:gap-4',
            healthy ? 'bg-success-50 border border-success-200' : 'bg-warning-50 border border-warning-200',
          )}>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={clsx('w-10 h-10 rounded-full flex items-center justify-center shrink-0',
                healthy ? 'bg-success-100' : 'bg-warning-100')}>
                {healthy
                  ? <ShieldCheckIcon className="w-6 h-6 text-success-600" />
                  : <ExclamationTriangleIcon className="w-6 h-6 text-warning-600" />}
              </div>
              <div>
                <p className={clsx('font-medium text-sm sm:text-base',
                  healthy ? 'text-success-800' : 'text-warning-800')}>
                  {healthy ? 'جميع الأنظمة تعمل بشكل طبيعي' : 'بعض الأنظمة تحتاج انتباه'}
                </p>
                <p className={clsx('text-xs sm:text-sm',
                  healthy ? 'text-success-600' : 'text-warning-600')}>
                  آخر تحديث موقع: {systemHealth.lastPositionAge} • {onlineVehicles} مركبة متصلة
                  {!systemHealth.dbHealthy && ' • تعذّر الاتصال بقاعدة البيانات'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
              {[
                { v: systemHealth.uptimeFormatted, l: 'مدة التشغيل' },
                { v: `${systemHealth.gpsAccuracy}%`, l: 'مركبات مُبلِّغة',
                  sub: `${systemHealth.reportingVehicles}/${systemHealth.totalVehicles}` },
                { v: `${systemHealth.serverLoad}%`, l: 'حمل الخادم',
                  sub: `${systemHealth.cpuCount} نواة` },
                { v: `${systemHealth.memoryMB} م.ب`, l: 'الذاكرة' },
              ].map((m) => (
                <div key={m.l} className="text-center">
                  <p className="text-base sm:text-xl font-bold text-gray-900">{m.v}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{m.l}</p>
                  {m.sub && <p className="text-[11px] text-gray-400">{m.sub}</p>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Tampering and crashes, given their own card above everything else.
          These are the alerts an operator must act on immediately, and in a
          mixed list they were indistinguishable from a coverage blip. */}
      {(tamperAlerts?.length ?? 0) > 0 && (
        <CollapsibleCard
          storageKey="critical-alerts"
          className="border-2 border-emergency-500 bg-white shadow-sm"
          headerClassName="px-4 py-3 bg-emergency-600 text-white"
          bodyClassName="divide-y divide-emergency-100 max-h-80 overflow-y-auto"
          title={(
            <span className="font-bold flex items-center gap-2">
              <ShieldExclamationIcon className="w-6 h-6" />
              تنبيهات حرجة تتطلب تدخلاً ({tamperAlerts!.length})
            </span>
          )}
          headerExtra={(
            <Link to="/alerts" className="text-xs underline text-white/90 hover:text-white">
              عرض كل التنبيهات
            </Link>
          )}
        >
          <>
            {tamperAlerts!.map((a: any) => (
              <div key={a.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-10 h-10 rounded-full bg-emergency-600 text-white flex items-center justify-center shrink-0">
                    {alertTypeIcon(a.type)}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-emergency-800">{alertTypeName(a.type)}</p>
                    <p className="text-xs text-gray-600 truncate">
                      {a.vehicle?.plateNumber || 'مركبة غير محددة'} • {dayjs(a.createdAt).fromNow()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 max-w-[16rem] truncate">
                    {a.messageAr || a.message}
                  </span>
                  {a.vehicleId && (
                    <Link to={`/map?vehicle=${a.vehicleId}`}
                          className="btn btn-sm btn-outline text-emergency-700 shrink-0 gap-1">
                      <MapPinIcon className="w-4 h-4" />
                      عرض على الخريطة
                    </Link>
                  )}
                  {/* Close it from here rather than making the operator open
                      the alerts page for a one-click action. Resolving is what
                      removes it from this card — the card lists unresolved
                      tampering only. */}
                  <button
                    onClick={() => resolveAlertMutation.mutate(a.id)}
                    disabled={resolveAlertMutation.isPending}
                    className="btn btn-sm btn-outline text-success-700 shrink-0 gap-1 disabled:opacity-50"
                    title="وضع حل للتنبيه وإخفاؤه من هذه القائمة"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    تم الحل
                  </button>
                </div>
              </div>
            ))}
          </>
        </CollapsibleCard>
      )}

      {/* Vehicles outside their organization's operating radius. */}
      {(zoneBreachData?.items?.length ?? 0) > 0 && (
        <CollapsibleCard
          storageKey="zone-breaches"
          className="border-2 border-emergency-300 bg-emergency-50"
          headerClassName="px-4 py-3 bg-emergency-100 text-emergency-800"
          bodyClassName="divide-y divide-emergency-200 max-h-80 overflow-y-auto"
          title={(
            <span className="font-bold flex items-center gap-2">
              <ExclamationTriangleIcon className="w-6 h-6" />
              مركبات خارج نطاق الجهة ({zoneBreachData!.items.length})
            </span>
          )}
          headerExtra={(
            <span className="text-xs text-emergency-700">
              يُحتسب من آخر موقع مسجَّل لكل مركبة
            </span>
          )}
        >
          <>
            {zoneBreachData!.items.map((b: any) => (
              <div key={b.vehicleId} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <TruckIcon className="w-5 h-5 text-emergency-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">{b.plateNumber}</p>
                    <p className="text-xs text-gray-600 truncate">
                      {b.organizationName} • الحد المسموح {b.radiusKm} كم
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-emergency-700">{b.distanceKm} كم</p>
                    <p className="text-[11px] text-gray-500">المسافة من المركز</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-emergency-700">+{b.overBy}</p>
                    <p className="text-[11px] text-gray-500">تجاوز بالكيلومتر</p>
                  </div>
                  {/* In-app first: the live map already accepts ?vehicle=<id>
                      and selects that unit. The Google Maps link stays for
                      turn-by-turn navigation, which is a different job. */}
                  <Link to={`/map?vehicle=${b.vehicleId}`}
                        className="btn btn-sm btn-outline text-emergency-700 shrink-0 gap-1">
                    <MapPinIcon className="w-4 h-4" />
                    عرض على الخريطة
                  </Link>
                  <a href={`https://www.google.com/maps?q=${b.latitude},${b.longitude}`}
                     target="_blank" rel="noopener noreferrer"
                     title="فتح في خرائط Google"
                     className="btn btn-sm btn-outline text-gray-600 shrink-0">
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </>
        </CollapsibleCard>
      )}

      {/* The check cannot run for entities with no centre point on file. */}
      {(zoneBreachData?.meta?.unconfigured ?? 0) > 0 && (
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 flex items-start gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-warning-600 shrink-0 mt-0.5" />
          <p className="text-sm text-warning-800">
            {zoneBreachData!.meta.unconfigured} جهة بلا إحداثيات مسجّلة — مراقبة النطاق معطّلة لمركباتها
            حتى تُدخل إحداثيات مركزها من صفحة الجهات.
          </p>
        </div>
      )}

      {/* Vehicles we have not heard from. Deliberately amber, not red: silence
          is usually a parked vehicle or a coverage hole, not a fault — but it
          is the state an operator most often needs to chase up. */}
      {(noSignalData?.items?.length ?? 0) > 0 && (
        <CollapsibleCard
          storageKey="no-signal"
          className="border-2 border-warning-300 bg-warning-50"
          headerClassName="px-4 py-3 bg-warning-100 text-warning-800"
          bodyClassName="divide-y divide-warning-200 max-h-80 overflow-y-auto"
          title={(
            <span className="font-bold flex items-center gap-2">
              <SignalSlashIcon className="w-6 h-6" />
              مركبات بلا إشارة ({noSignalData!.items.length})
            </span>
          )}
          headerExtra={(
            <span className="text-xs text-warning-700">
              لم يصل منها شيء منذ أكثر من {noSignalData?.meta?.hours ?? 6} ساعات
            </span>
          )}
        >
          <>
            {noSignalData!.items.map((v: any) => (
              <div key={v.vehicleId} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <TruckIcon className="w-5 h-5 text-warning-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">{v.plateNumber}</p>
                    <p className="text-xs text-gray-600 truncate">{v.organizationName || 'جهة غير محددة'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    {v.neverReported ? (
                      <p className="text-sm font-bold text-warning-800">لم ترسل إشارة قط</p>
                    ) : (
                      <>
                        {/* Both forms: the absolute time is what gets written
                            into a report, the relative one is what an operator
                            reads at a glance. */}
                        <p className="text-sm font-bold text-warning-800" dir="ltr">
                          {dayjs(v.lastSeenAt).tz(TZ).format('YYYY/MM/DD HH:mm')}
                        </p>
                        <p className="text-[11px] text-gray-500">منذ {v.silentLabel}</p>
                      </>
                    )}
                  </div>
                  <Link to={`/map?vehicle=${v.vehicleId}`}
                        className="btn btn-sm btn-outline text-warning-700 shrink-0 gap-1">
                    <MapPinIcon className="w-4 h-4" />
                    آخر موقع
                  </Link>
                </div>
              </div>
            ))}
          </>
        </CollapsibleCard>
      )}

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 3xl:gap-6 4xl:gap-8 5xl:gap-10">
        <KPICard
          title="إجمالي المركبات"
          value={stats?.vehicles?.total || 0}
          subtitle={`${onlineVehicles} متصلة الآن`}
          icon={<TruckIcon className="w-6 h-6" />}
          color="primary"
          to="/vehicles"
        />
        <KPICard
          title="التنبيهات النشطة"
          value={stats?.alerts?.unread || 0}
          subtitle={`${stats?.alerts?.critical24h || 0} حرجة`}
          icon={<BellAlertIcon className="w-6 h-6" />}
          color={(stats?.alerts?.critical24h || 0) > 0 ? 'danger' : 'warning'}
          to="/alerts?status=unread"
        />
        <KPICard
          title="المركبات المتصلة"
          value={onlineVehicles}
          subtitle={`من ${stats?.vehicles?.total || 0} مركبة`}
          icon={<WifiIcon className="w-6 h-6" />}
          color="success"
          to="/vehicles?connectivity=online"
        />
        <KPICard
          title="الرحلات"
          value={reportSummary?.totalTrips || 0}
          subtitle={selectedPeriod === 'today' ? 'اليوم' : selectedPeriod === 'week' ? 'هذا الأسبوع' : 'هذا الشهر'}
          icon={<MapPinIcon className="w-6 h-6" />}
          color="purple"
          to={`/reports?tab=vehicles&period=${selectedPeriod}`}
        />
        <KPICard
          title="المسافة الإجمالية"
          value={`${reportSummary?.totalDistance || 0} كم`}
          subtitle={selectedPeriod === 'today' ? 'اليوم' : selectedPeriod === 'week' ? 'هذا الأسبوع' : 'هذا الشهر'}
          icon={<GlobeAltIcon className="w-6 h-6" />}
          color="gray"
          to={`/reports?tab=trips&period=${selectedPeriod}`}
        />
        <KPICard
          title="استخدام الأسطول"
          value={`${fleetUtilization}%`}
          subtitle="نسبة المركبات في مهمة"
          icon={<ChartBarIcon className="w-6 h-6" />}
          color={fleetUtilization > 70 ? 'success' : fleetUtilization > 40 ? 'warning' : 'danger'}
          to="/reports?tab=performance"
        />
      </div>

      {/* KPI Cards - Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="متوسط الاستجابة"
          value={`${avgResponseTime.toFixed(1)} د`}
          subtitle="متوسط وقت الاستجابة"
          icon={<ClockIcon className="w-6 h-6" />}
          color={avgResponseTime < 10 ? 'success' : avgResponseTime < 15 ? 'warning' : 'danger'}
        />
        <KPICard
          title="الجهات"
          value={stats?.organizations || 0}
          subtitle="جهة مسجلة"
          icon={<BuildingOffice2Icon className="w-6 h-6" />}
          color="primary"
          to="/organizations"
        />
        <KPICard
          title="المستخدمين"
          value={stats?.users || 0}
          subtitle="مستخدم نشط"
          icon={<UserGroupIcon className="w-6 h-6" />}
          color="purple"
          to="/users"
        />
        <KPICard
          title="حالة النظام"
          value={systemHealth.dbHealthy && systemHealth.serverLoad < 85 ? 'ممتاز' : 'يحتاج مراجعة'}
          subtitle={`حمل الخادم ${systemHealth.serverLoad}% • تشغيل ${systemHealth.uptimeFormatted}`}
          icon={<CpuChipIcon className="w-6 h-6" />}
          color={systemHealth.dbHealthy && systemHealth.serverLoad < 85 ? 'success' : 'warning'}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 3xl:gap-8 4xl:gap-10 5xl:gap-12">
        {/* Activity Area Chart */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="card-title flex items-center gap-2 3xl:gap-3">
              <AreaChart className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10 text-primary-500" />
              النشاط اليومي
            </h3>
            <span className="text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl text-gray-500">
              {selectedPeriod === 'today' ? 'اليوم' : selectedPeriod === 'week' ? 'آخر 7 أيام' : 'آخر 30 يوم'}
            </span>
          </div>
          <div className="card-body">
            {activityChartData && activityChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280} className="sm:!h-[300px] 3xl:!h-[400px] 4xl:!h-[500px] 5xl:!h-[600px]">
              <ComposedChart data={activityChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    direction: 'rtl'
                  }} 
                />
                <Legend />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="trips"
                  name="الرحلات"
                  fill="#3b82f6"
                  fillOpacity={0.3}
                  stroke="#3b82f6"
                  strokeWidth={2}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="distance"
                  name="المسافة (كم)"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ fill: '#22c55e' }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="alerts"
                  name="التنبيهات"
                  fill="#f59e0b"
                  opacity={0.7}
                />
              </ComposedChart>
            </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-500">
                لا توجد بيانات للعرض
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Status Pie Chart */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="card-title flex items-center gap-2 3xl:gap-3">
              <SignalIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10 text-success-500" />
              حالة المركبات
            </h3>
            <span className="badge badge-success">{onlineVehicles} متصلة</span>
          </div>
          <div className="card-body">
            {vehicleStatusData && vehicleStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280} className="sm:!h-[300px] 3xl:!h-[400px] 4xl:!h-[500px] 5xl:!h-[600px]">
              <PieChart>
                <Pie
                  data={vehicleStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {vehicleStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-500">
                لا توجد بيانات للعرض
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 3xl:gap-8 4xl:gap-10 5xl:gap-12">
        {/* Hourly Distribution */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2 3xl:gap-3">
              <BoltIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10 text-warning-500" />
              التوزيع الساعي
            </h3>
          </div>
          <div className="card-body">
            {hourlyChartData && hourlyChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={230} className="sm:!h-[250px] 3xl:!h-[350px] 4xl:!h-[450px] 5xl:!h-[550px]">
              <BarChart data={hourlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" name="النشاط" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">
                لا توجد بيانات للعرض
              </div>
            )}
          </div>
        </div>

        {/* Alerts by Severity */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2 3xl:gap-3">
              <FireIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10 text-emergency-500" />
              التنبيهات حسب الخطورة
            </h3>
          </div>
          <div className="card-body">
            {alertsBySeverity && alertsBySeverity.length > 0 ? (
            <ResponsiveContainer width="100%" height={230} className="sm:!h-[250px] 3xl:!h-[350px] 4xl:!h-[450px] 5xl:!h-[550px]">
              <PieChart>
                <Pie
                  data={alertsBySeverity}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                >
                  {alertsBySeverity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">
                لا توجد بيانات للعرض
              </div>
            )}
          </div>
        </div>

        {/* System Health Radial */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2">
              <HeartIcon className="w-5 h-5 text-success-500" />
              صحة النظام
            </h3>
          </div>
          <div className="card-body">
            {healthData && healthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="30%"
                outerRadius="90%"
                data={healthData}
                startAngle={180}
                endAngle={0}
              >
                <RadialBar
                  background
                  dataKey="value"
                  cornerRadius={10}
                />
                <Legend
                  iconSize={10}
                  layout="horizontal"
                  verticalAlign="bottom"
                />
                <Tooltip />
              </RadialBarChart>
            </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">
                لا توجد بيانات للعرض
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alerts Trend with LineChart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts Trend */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2">
              <ArrowTrendingDownIcon className="w-5 h-5 text-primary-500" />
              اتجاه التنبيهات
            </h3>
          </div>
          <div className="card-body">
            {activityChartData && activityChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={activityChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="alerts" name="التنبيهات" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="resolved" name="تم حلها" stroke="#22c55e" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">
                لا توجد بيانات للعرض
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Types Distribution */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2">
              <TruckIcon className="w-5 h-5 text-primary-500" />
              توزيع أنواع المركبات
            </h3>
          </div>
          <div className="card-body">
            {vehicleTypesData && vehicleTypesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={vehicleTypesData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="count"
                  nameKey="type"
                  label={({ type, count }) => `${type}: ${count}`}
                >
                  {vehicleTypesData.map((_entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">
                لا توجد بيانات للعرض
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Alerts */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="card-title flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-warning-500" />
              آخر التنبيهات
            </h3>
            <Link to="/alerts" className="text-sm text-primary-600 hover:text-primary-700">
              عرض الكل
            </Link>
          </div>
          <div className="card-body p-0">
            <div className="divide-y divide-gray-100">
              {recentAlerts && recentAlerts.length > 0 ? (
                recentAlerts.slice(0, 5).map((alert: any) => (
                  <div key={alert.id} className={clsx('flex items-center gap-3 p-4',
                    isTamperAlert(alert.type) && !alert.isResolved
                      ? 'bg-emergency-50 border-r-4 border-r-emergency-600 hover:bg-emergency-100'
                      : 'hover:bg-gray-50')}>
                    <div className={clsx(
                      'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
                      isTamperAlert(alert.type) && !alert.isResolved
                        ? 'bg-emergency-600 text-white'
                        : severityColors[alert.severity]
                    )}>
                      {alertTypeIcon(alert.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {alertTypeName(alert.type)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {alert.vehicle?.plateNumber || 'غير محدد'} • {dayjs(alert.createdAt).fromNow()}
                      </p>
                    </div>
                    {!alert.isRead && (
                      <span className="w-2 h-2 bg-primary-500 rounded-full" />
                    )}
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <CheckCircleIcon className="w-12 h-12 mx-auto text-success-300 mb-2" />
                  <p>لا توجد تنبيهات حديثة</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top Vehicles */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="card-title flex items-center gap-2">
              <ArrowTrendingUpIcon className="w-5 h-5 text-success-500" />
              أكثر المركبات نشاطاً
            </h3>
            <span className="text-xs text-gray-500">
              {selectedPeriod === 'today' ? 'اليوم' : selectedPeriod === 'week' ? 'هذا الأسبوع' : 'هذا الشهر'}
            </span>
          </div>
          <div className="card-body p-0">
            <div className="divide-y divide-gray-100">
              {topVehicles.length > 0 ? (
                topVehicles.map((vehicle: any, index: number) => (
                  <div key={index} className="flex items-center gap-3 p-4 hover:bg-gray-50">
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{vehicle.name}</p>
                      <p className="text-xs text-gray-500">
                        {vehicle.trips} رحلة • {vehicle.distance} كم
                      </p>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-bold text-gray-900">{vehicle.activeDays}</p>
                      <p className="text-xs text-gray-500">يوم نشط</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <TruckIcon className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p>لا توجد بيانات متاحة</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Footer */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDaysIcon className="w-5 h-5" />
            <span className="text-sm opacity-90">اليوم</span>
          </div>
          <p className="text-2xl font-bold">{dayjs().format('DD MMMM YYYY')}</p>
        </div>
        <div className="bg-gradient-to-br from-success-500 to-success-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <SignalIcon className="w-5 h-5" />
            <span className="text-sm opacity-90">الاتصال</span>
          </div>
          <p className="text-2xl font-bold">{Math.round((onlineVehicles / (stats?.vehicles?.total || 1)) * 100)}%</p>
        </div>
        <div className="bg-gradient-to-br from-warning-500 to-warning-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <BoltIcon className="w-5 h-5" />
            <span className="text-sm opacity-90">النشاط</span>
          </div>
          <p className="text-2xl font-bold">{reportSummary?.totalTrips || 0} رحلة</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <GlobeAltIcon className="w-5 h-5" />
            <span className="text-sm opacity-90">المسافة</span>
          </div>
          <p className="text-2xl font-bold">{reportSummary?.totalDistance || 0} كم</p>
        </div>
      </div>
    </div>
  );
}
